import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, TwoFactorRequiredError } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { LogIn, Sparkles, Shield } from "lucide-react";
import { ForgotPasswordForm } from "@/components/ForgotPasswordForm";
import { GoogleLoginButton } from "@/components/GoogleLoginButton";
import { useLocation } from "wouter";

export default function AuthPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  const { login, isLoginLoading, loginError } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await login({
        email: formData.email,
        password: formData.password,
        ...(requires2FA && totpCode ? { totpCode } : {}),
      });
      toast({
        title: t("auth.loginSuccess"),
        description: t("auth.loginSuccessDesc"),
      });
    } catch (error: any) {
      if (error instanceof TwoFactorRequiredError || error?.name === "TwoFactorRequiredError") {
        setRequires2FA(true);
        return;
      }
      toast({
        title: t("auth.loginError"),
        description: t("auth.loginErrorDesc"),
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleGoogleSuccess = (user: any, token: string) => {
    toast({
      title: t("auth.loginSuccess"),
      description: t("auth.loginSuccessNamed", { name: user.username }),
    });
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center p-4">
        <ForgotPasswordForm onBackToLogin={() => setShowForgotPassword(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-background border border-border shadow-xl">
        <CardHeader className="text-center space-y-4">
          <img
            src="https://pub-9c0a0846a5c24484849bf7e3f13e4239.r2.dev/viralpartners/logo-800w.webp"
            alt="Viral Partners Logo"
            className="mx-auto h-16 object-contain"
          />
          <CardTitle className="text-heading-2 font-extrabold bg-gradient-to-r from-red-500 to-red-400 bg-clip-text text-transparent">
            Viral Partners
          </CardTitle>
          <p className="text-muted-foreground">{t("auth.loginSubtitle")}</p>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground/80">
                {t("auth.email")}
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="bg-background border-border text-foreground"
                placeholder={t("auth.emailPlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground/80">
                {t("auth.password")}
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="bg-background border-border text-foreground"
                placeholder={t("auth.passwordPlaceholder")}
              />
            </div>

            {requires2FA && (
              <div className="space-y-2">
                <Label htmlFor="totpCode" className="text-foreground/80 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-yellow-500" />
                  {t("auth.twoFactorTitle")}
                </Label>
                <Input
                  id="totpCode"
                  type="text"
                  inputMode="numeric"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  maxLength={8}
                  required
                  className="bg-background border-border text-foreground text-center text-lg tracking-widest"
                  placeholder="000000"
                  autoFocus
                />
                <p className="text-muted-foreground text-xs">{t("auth.twoFactorHint")}</p>
              </div>
            )}

            {loginError && (
              <div className="text-red-600 dark:text-red-400 text-sm">
                {loginError?.message === "2FA_REQUIRED" ? null : loginError?.message}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white flex items-center justify-center space-x-2"
              disabled={isLoginLoading}
              data-testid="button-login"
            >
              <LogIn className="w-4 h-4" />
              <span>{isLoginLoading ? t("auth.loginInProgress") : t("auth.loginButton")}</span>
            </Button>
          </form>

          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-forgot-password"
            >
              {t("auth.forgotPassword")}
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">{t("auth.or")}</span>
              </div>
            </div>

            <div className="mt-4">
              <GoogleLoginButton onSuccess={handleGoogleSuccess} />
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  {t("auth.noAccount")}
                </span>
              </div>
            </div>

            <Button
              onClick={() => setLocation("/register")}
              className="w-full bg-gradient-to-r from-red-500/80 to-red-600/80 hover:from-red-500 hover:to-red-600 text-white flex items-center justify-center space-x-2"
              data-testid="button-trial"
            >
              <Sparkles className="w-4 h-4" />
              <span>{t("auth.tryFree")}</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
